import React, { useState, useEffect, useCallback } from 'react';
import {
  Lock, ArrowUpRight, FileText, Plus, Eye, Trash2, Edit3,
  RefreshCw, Sparkles, Clock, CheckCircle, Globe, EyeOff,
  PenTool, Users, Send,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import '../styles/admin-blog.css';

const API = process.env.REACT_APP_BACKEND_URL;

/* ====== Password Gate ====== */
function PasswordGate({ onUnlock }) {
  const [pw, setPw] = useState('');
  const [error, setError] = useState(false);
  const handleSubmit = (e) => {
    e.preventDefault();
    if (pw.trim()) { setError(false); onUnlock(pw.trim()); }
    else { setError(true); }
  };
  return (
    <div className="ab-gate" data-testid="blog-admin-password-gate">
      <div className="ab-gate__orbs">
        <div className="ab-gate__orb ab-gate__orb--1" />
        <div className="ab-gate__orb ab-gate__orb--2" />
      </div>
      <div className="ab-gate__card">
        <div className="ab-gate__icon"><PenTool size={24} /></div>
        <h2>Blog Manager</h2>
        <p>Enter admin password</p>
        <form onSubmit={handleSubmit}>
          <input
            type="password" value={pw} onChange={e => setPw(e.target.value)}
            placeholder="••••••••" className={error ? 'ab-gate__input--error' : ''}
            autoFocus data-testid="blog-admin-password-input"
          />
          <button type="submit" data-testid="blog-admin-password-submit">
            Unlock <ArrowUpRight size={15} />
          </button>
        </form>
      </div>
    </div>
  );
}

/* ====== Main Dashboard ====== */
export default function AdminBlog() {
  const [password, setPassword] = useState(null);
  const [tab, setTab] = useState('posts');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);

  // AI Generate state
  const [topic, setTopic] = useState('');
  const [instructions, setInstructions] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatedPost, setGeneratedPost] = useState(null);

  // Editor state
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);

  // Preview
  const [previewPost, setPreviewPost] = useState(null);

  const fetchPosts = useCallback(async () => {
    if (!password) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/blog/admin/posts?password=${password}`);
      if (res.status === 401) { setPassword(null); return; }
      const data = await res.json();
      setPosts(data.posts || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    finally { setLoading(false); }
  }, [password]);

  useEffect(() => { if (password) fetchPosts(); }, [password, fetchPosts]);

  const handleGenerate = async () => {
    if (!topic.trim()) return;
    setGenerating(true);
    setGeneratedPost(null);
    try {
      const res = await fetch(`${API}/api/blog/admin/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password,
          topic: topic.trim(),
          additional_instructions: instructions.trim() || undefined,
        }),
      });
      if (!res.ok) throw new Error('Generation failed');
      const data = await res.json();
      setGeneratedPost(data);
      fetchPosts();
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error('Generate error:', e);
      alert('Failed to generate post.');
    } finally { setGenerating(false); }
  };

  const handleTogglePublish = async (slug) => {
    try {
      const res = await fetch(`${API}/api/blog/admin/${slug}/publish?password=${password}`, { method: 'PUT' });
      if (res.ok) fetchPosts();
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Publish toggle error:', e); }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Delete this post permanently?')) return;
    try {
      await fetch(`${API}/api/blog/admin/${slug}?password=${password}`, { method: 'DELETE' });
      fetchPosts();
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Delete error:', e); }
  };

  const handleEditOpen = (post) => {
    setEditingPost({ ...post });
    setTab('editor');
  };

  const handleSave = async () => {
    if (!editingPost) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/blog/admin/save?password=${password}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingPost),
      });
      if (res.ok) {
        fetchPosts();
        setTab('posts');
        setEditingPost(null);
      }
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Save error:', e); }
    finally { setSaving(false); }
  };

  if (!password) return <PasswordGate onUnlock={setPassword} />;

  const publishedCount = posts.filter(p => p.status === 'published').length;
  const draftCount = posts.filter(p => p.status === 'draft').length;

  return (
    <div className="ab-dash" data-testid="blog-admin-dashboard">
      {/* Sidebar */}
      <aside className="ab-sidebar">
        <div className="ab-sidebar__brand">
          <PenTool size={20} />
          <span>Blog Manager</span>
        </div>
        <nav className="ab-sidebar__nav">
          {[
            { id: 'posts', icon: FileText, label: 'All Posts' },
            { id: 'generate', icon: Sparkles, label: 'AI Writer' },
            { id: 'editor', icon: Edit3, label: 'Editor', hidden: !editingPost },
          ].filter(i => !i.hidden).map(item => (
            <button
              key={item.id}
              className={`ab-sidebar__item ${tab === item.id ? 'ab-sidebar__item--active' : ''}`}
              onClick={() => setTab(item.id)}
              data-testid={`ab-tab-${item.id}`}
            >
              <item.icon size={17} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="ab-sidebar__links">
          <a href="/admin/newsletter" className="ab-sidebar__back">Newsletter</a>
          <a href="/admin/analytics" className="ab-sidebar__back">Analytics</a>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ab-main">
        {/* Header */}
        <header className="ab-header">
          <div>
            <h1 className="ab-header__title">
              {tab === 'posts' && 'Blog Posts'}
              {tab === 'generate' && 'AI Blog Writer'}
              {tab === 'editor' && (editingPost ? `Editing: ${editingPost.title}` : 'Editor')}
            </h1>
            <p className="ab-header__sub">
              {tab === 'posts' && `${publishedCount} published · ${draftCount} drafts`}
              {tab === 'generate' && 'Generate blog posts with consistent Skye AI voice'}
              {tab === 'editor' && 'Edit post details and content'}
            </p>
          </div>
          <button className="ab-header__refresh" onClick={fetchPosts}>
            <RefreshCw size={16} /> Refresh
          </button>
        </header>

        {/* POSTS TAB */}
        {tab === 'posts' && (
          <div className="ab-posts" data-testid="ab-posts-section">
            {posts.length > 0 ? (
              <div className="ab-posts-list">
                {posts.map((post) => (
                  <div key={post.slug} className="ab-post-item" data-testid={`ab-post-${post.slug}`}>
                    <div className="ab-post-item__info">
                      <div className="ab-post-item__title-row">
                        <h4>{post.title}</h4>
                        <span className={`ab-badge ${post.status === 'published' ? 'ab-badge--pub' : 'ab-badge--draft'}`}>
                          {post.status}
                        </span>
                      </div>
                      <p className="ab-post-item__desc">{post.description}</p>
                      <div className="ab-post-item__meta">
                        <span>{post.category}</span>
                        <span>·</span>
                        <span>{post.date}</span>
                        <span>·</span>
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                    <div className="ab-post-item__actions">
                      <button className="ab-btn ab-btn--small" onClick={() => handleEditOpen(post)} title="Edit">
                        <Edit3 size={14} /> Edit
                      </button>
                      <button className="ab-btn ab-btn--small" onClick={() => setPreviewPost(post)} title="Preview">
                        <Eye size={14} />
                      </button>
                      <button
                        className={`ab-btn ab-btn--small ${post.status === 'published' ? 'ab-btn--warning' : 'ab-btn--primary'}`}
                        onClick={() => handleTogglePublish(post.slug)}
                        title={post.status === 'published' ? 'Unpublish' : 'Publish'}
                      >
                        {post.status === 'published' ? <EyeOff size={14} /> : <Globe size={14} />}
                        {post.status === 'published' ? 'Unpublish' : 'Publish'}
                      </button>
                      <button className="ab-btn ab-btn--small ab-btn--danger" onClick={() => handleDelete(post.slug)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="ab-empty">
                <FileText size={32} />
                <p>No blog posts yet</p>
                <span>Use the AI Writer to generate your first post</span>
              </div>
            )}
          </div>
        )}

        {/* GENERATE TAB */}
        {tab === 'generate' && (
          <div className="ab-generate" data-testid="ab-generate-section">
            <div className="ab-card">
              <div className="ab-card__header">
                <Sparkles size={18} />
                <h3>AI Blog Writer</h3>
              </div>
              <div className="ab-card__body">
                <label className="ab-label">Topic <span className="ab-label--req">*</span></label>
                <input
                  type="text"
                  className="ab-input"
                  placeholder="e.g. 'How AI chatbots reduce no-shows for dental offices'"
                  value={topic}
                  onChange={e => setTopic(e.target.value)}
                  data-testid="ab-topic-input"
                />

                <label className="ab-label" style={{ marginTop: 16 }}>Additional Instructions <span className="ab-label--opt">(optional)</span></label>
                <textarea
                  className="ab-textarea"
                  rows={3}
                  placeholder="e.g. 'Focus on ROI numbers, mention our case study with Tony Lopez'"
                  value={instructions}
                  onChange={e => setInstructions(e.target.value)}
                  data-testid="ab-instructions-input"
                />

                <button
                  className="ab-btn ab-btn--primary"
                  onClick={handleGenerate}
                  disabled={generating || !topic.trim()}
                  data-testid="ab-generate-btn"
                >
                  {generating ? (
                    <><RefreshCw size={16} className="ab-spin" /> Generating...</>
                  ) : (
                    <><Sparkles size={16} /> Generate Blog Post</>
                  )}
                </button>
              </div>
            </div>

            {/* Generated Preview */}
            {generatedPost && (
              <div className="ab-card ab-card--generated" data-testid="ab-generated-preview">
                <div className="ab-card__header">
                  <CheckCircle size={18} style={{ color: '#10B981' }} />
                  <h3>Generated — Saved as Draft</h3>
                </div>
                <div className="ab-card__body">
                  <div className="ab-gen__meta">
                    <h2>{generatedPost.title}</h2>
                    <p className="ab-gen__desc">{generatedPost.description}</p>
                    <div className="ab-gen__tags">
                      <span className="ab-badge ab-badge--draft">Draft</span>
                      <span className="ab-badge ab-badge--cat">{generatedPost.category}</span>
                      <span>{generatedPost.readTime}</span>
                    </div>
                  </div>
                  <div className="ab-gen__content">
                    <ReactMarkdown>{generatedPost.content}</ReactMarkdown>
                  </div>
                  <div className="ab-gen__actions">
                    <button className="ab-btn ab-btn--primary" onClick={() => handleTogglePublish(generatedPost.slug)}>
                      <Globe size={16} /> Publish Now
                    </button>
                    <button className="ab-btn" onClick={() => {
                      const p = posts.find(p => p.slug === generatedPost.slug);
                      if (p) handleEditOpen(p);
                      else fetchPosts().then(() => setTab('posts'));
                    }}>
                      <Edit3 size={16} /> Edit First
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* EDITOR TAB */}
        {tab === 'editor' && editingPost && (
          <div className="ab-editor" data-testid="ab-editor-section">
            <div className="ab-card">
              <div className="ab-card__header">
                <Edit3 size={18} />
                <h3>Edit Post</h3>
              </div>
              <div className="ab-card__body">
                <div className="ab-editor__grid">
                  <div className="ab-editor__field">
                    <label className="ab-label">Title</label>
                    <input
                      type="text" className="ab-input"
                      value={editingPost.title}
                      onChange={e => setEditingPost({ ...editingPost, title: e.target.value })}
                      data-testid="ab-edit-title"
                    />
                  </div>
                  <div className="ab-editor__field">
                    <label className="ab-label">Slug</label>
                    <input
                      type="text" className="ab-input"
                      value={editingPost.slug}
                      onChange={e => setEditingPost({ ...editingPost, slug: e.target.value })}
                      data-testid="ab-edit-slug"
                    />
                  </div>
                  <div className="ab-editor__field">
                    <label className="ab-label">Description (meta)</label>
                    <textarea
                      className="ab-textarea" rows={2}
                      value={editingPost.description}
                      onChange={e => setEditingPost({ ...editingPost, description: e.target.value })}
                      data-testid="ab-edit-description"
                    />
                  </div>
                  <div className="ab-editor__row">
                    <div className="ab-editor__field">
                      <label className="ab-label">Category</label>
                      <select
                        className="ab-select"
                        value={editingPost.category}
                        onChange={e => setEditingPost({ ...editingPost, category: e.target.value })}
                      >
                        {["AI Strategy", "Revenue", "Growth", "Automation", "Case Studies", "Industry Insights", "Product Updates"].map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    <div className="ab-editor__field">
                      <label className="ab-label">Read Time</label>
                      <input type="text" className="ab-input" value={editingPost.readTime}
                        onChange={e => setEditingPost({ ...editingPost, readTime: e.target.value })} />
                    </div>
                    <div className="ab-editor__field">
                      <label className="ab-label">Status</label>
                      <select className="ab-select" value={editingPost.status}
                        onChange={e => setEditingPost({ ...editingPost, status: e.target.value })}>
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </select>
                    </div>
                  </div>
                  <div className="ab-editor__field">
                    <label className="ab-label">Content (Markdown)</label>
                    <textarea
                      className="ab-textarea ab-textarea--content"
                      rows={20}
                      value={editingPost.content}
                      onChange={e => setEditingPost({ ...editingPost, content: e.target.value })}
                      data-testid="ab-edit-content"
                    />
                  </div>
                </div>
                <div className="ab-editor__actions">
                  <button className="ab-btn ab-btn--primary" onClick={handleSave} disabled={saving} data-testid="ab-save-btn">
                    {saving ? <><RefreshCw size={16} className="ab-spin" /> Saving...</> : <><CheckCircle size={16} /> Save Post</>}
                  </button>
                  <button className="ab-btn" onClick={() => setPreviewPost(editingPost)}>
                    <Eye size={16} /> Preview
                  </button>
                  <button className="ab-btn ab-btn--ghost" onClick={() => { setEditingPost(null); setTab('posts'); }}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {previewPost && (
          <div className="ab-preview-overlay" onClick={() => setPreviewPost(null)}>
            <div className="ab-preview-modal" onClick={e => e.stopPropagation()}>
              <div className="ab-preview-modal__header">
                <h3>Post Preview</h3>
                <button onClick={() => setPreviewPost(null)}>&times;</button>
              </div>
              <div className="ab-preview-modal__body" data-testid="ab-preview-content">
                <h1>{previewPost.title}</h1>
                <p style={{ color: '#71717a', marginBottom: 24 }}>{previewPost.description}</p>
                <ReactMarkdown>{previewPost.content}</ReactMarkdown>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
